import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ApiError, BuildingData, DataWithUpload, Draft, useBuildingMutation, useStore } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { BuildingForm, BuildingHeader, LayoutHeader, NavigationDelete } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const BuildingEdit = () => {
  const [confirmDelete, setConfirmDelete] = useState<Draft<BuildingData>>()
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const { message } = useStore()
  const [{ companyId, locationId, buildingId }] = useNumberParams()
  const mutation = useBuildingMutation({ companyId })

  const { t } = useTranslation()

  const save = async (draft: Draft<DataWithUpload<BuildingData>>) => {
    setErrors({})
    const { upload, ...otherData } = draft.data
    if (!draft.data.upload && draft.data.src) {
      delete otherData.src
    }
    if (upload || upload === null) {
      try {
        await mutation.upload(draft, upload)
        delete otherData.src
      } catch (error) {
        console.debug(`BUILDING: Building failed: ${(error as Error).message}`)
        const { message } = error as ApiError
        setErrors({ src: message })
        draft.update('progress', undefined)
        return
      }
    }
    try {
      await mutation.update(new Draft(otherData))
      draft.settle()
      message.add({
        message: t('Building was updated successfully'),
        status: 'success',
      })
      router.push(`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`)
    } catch (error) {
      const { fields, message: errorMessage } = error as ApiError
      console.debug(`BUILDING: Building failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Building could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
    }
  }

  const remove = async () => {
    try {
      await mutation.delete(confirmDelete!)
      message.add({
        message: t('Building was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`BUILDING: Building deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Building could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    router.push(`/companies/${companyId}/locations/${locationId}`)
  }

  const uploadError = (errors?: Record<string, string>) => {
    setErrors(errors)
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <BuildingHeader
          companyId={companyId}
          locationId={locationId}
          buildingId={buildingId}
          breadcrumbItems={[{ title: t('Edit') }]}
        />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<BuildingForm.Skeleton numberOfbuttons={[2, 1]} />}>
          <BuildingForm
            companyId={companyId}
            locationId={locationId}
            buildingId={buildingId}
            onSave={save}
            cancelHref={`/companies/${companyId}/locations/${locationId}/buildings/${buildingId}`}
            onDelete={async (draft) => setConfirmDelete(draft)}
            onError={uploadError}
            errors={errors}
          />
        </Suspense>
        <NavigationDelete
          entity={t('Building')}
          visible={!!confirmDelete}
          onClose={() => setConfirmDelete(undefined)}
          onDelete={remove}
        />
      </View>
    </View>
  )
}

export default BuildingEdit
